import React, { useState } from 'react';
import { api, useMutation } from '../lib/convexApi';
import PhoneInput from './PhoneInput';

interface PhoneVerificationProps {
  onVerificationComplete: (player: any) => void;
}

const PhoneVerification: React.FC<PhoneVerificationProps> = ({ onVerificationComplete }) => {
  const sendVerificationCode = useMutation(api.players.sendVerificationCode);
  const verifyPlayer = useMutation(api.players.verifyPlayer);
  
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const getDefaultCountryCode = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('preferredCountryCode') || '+52';
    }
    return '+52';
  };

  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    countryCode: getDefaultCountryCode(),
    code: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const fullPhone = formData.countryCode + formData.phoneNumber.replace(/\D/g, '');
      const result = await sendVerificationCode({
        phoneNumber: fullPhone,
        name: formData.name,
      });

      // Code is now sent via WhatsApp
      if (result.message.includes('WhatsApp')) {
        // Production: Code sent via WhatsApp
        setStep('code');
      } else {
        // Fallback: Show code if WhatsApp is unavailable
        alert(`Verification code: ${result.code}\n\n${result.message}`);
        setStep('code');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const fullPhone = formData.countryCode + formData.phoneNumber.replace(/\D/g, '');
      const result = await verifyPlayer({
        phoneNumber: fullPhone,
        code: formData.code,
      });

      onVerificationComplete(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h3 className="text-xl font-bold text-gray-900 mb-6">Verify Your Phone Number</h3>
      
      {step === 'phone' ? (
        <form onSubmit={handleSendCode} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Your Name
            </label>
            <input
              type="text"
              id="name"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Número de Teléfono
            </label>
            <PhoneInput
              countryCode={formData.countryCode}
              phoneNumber={formData.phoneNumber}
              onCountryCodeChange={(code) => setFormData(prev => ({ ...prev, countryCode: code }))}
              onPhoneNumberChange={(number) => setFormData(prev => ({ ...prev, phoneNumber: number }))}
              required
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
          >
            {isLoading ? 'Sending Code...' : 'Send Verification Code'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleVerifyCode} className="space-y-4">
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
              Verification Code
            </label>
            <input
              type="text"
              id="code"
              required
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg font-mono"
              placeholder="123456"
              maxLength={6}
            />
            <p className="text-sm text-gray-500 mt-2">
              Enter the 6-digit code sent to {formData.countryCode + formData.phoneNumber}
            </p>
          </div>

          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}

          <div className="space-y-3">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
            >
              {isLoading ? 'Verifying...' : 'Verify Code'}
            </button>
            
            <button
              type="button"
              onClick={() => {
                setStep('phone');
                setError('');
                setFormData(prev => ({ ...prev, code: '' }));
              }}
              className="w-full text-blue-600 hover:text-blue-800 py-2"
            >
              ← Back to Phone Number
            </button>
          </div>
        </form>
      )}
      
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex">
          <div className="text-blue-500 mr-3">ℹ️</div>
          <div>
            <h4 className="text-sm font-semibold text-blue-800 mb-1">Why verify your phone?</h4>
            <p className="text-sm text-blue-700">
              We use your phone number to send WhatsApp notifications about tournament updates and to prevent duplicate registrations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneVerification;